import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import {
  Sparkles,
  Bell,
  Target,
  Brain,
  Image as ImageIcon,
  ArrowUpRight,
  AlertTriangle,
  Wand2,
  CheckCircle2,
  Search,
  Filter,
  Download as DownloadIcon,
  ExternalLink,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { formatINR } from "../lib/brand";

const TABS = [
  { id: "alerts", label: "Real-time Alerts", icon: Bell },
  { id: "conversion", label: "Conversion", icon: Target },
  { id: "decisions", label: "Decision Engine", icon: Brain },
  { id: "creative", label: "Creative AI", icon: Sparkles },
];

const PRIORITY_COLOR = {
  high: "bg-[#4F46E5]/15 border-[#4F46E5]/40 text-[#4F46E5]",
  medium: "bg-amber-500/15 border-amber-500/40 text-amber-400",
  low: "bg-white/5 border-white/20 text-white/70",
};

const COMMAND_STYLE = {
  "Scale this product": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  "Stop ads on this SKU": "bg-[#4F46E5]/15 text-[#4F46E5] border-[#4F46E5]/40",
  "Increase price by 5%": "bg-violet-500/15 text-violet-300 border-violet-500/40",
  "Improve product images": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  Monitor: "bg-white/5 text-white/60 border-white/20",
};

function TabButton({ active, icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
        active
          ? "bg-white/10 text-white border border-[#4F46E5]/40"
          : "text-white/60 hover:text-white hover:bg-white/5 border border-transparent"
      }`}
    >
      <Icon size={16} className={active ? "text-[#4F46E5]" : ""} />
      {label}
    </button>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-lg bg-[#4F46E5]/10 border border-[#4F46E5]/30 flex items-center justify-center">
        <Icon size={18} className="text-[#4F46E5]" />
      </div>
      <div>
        <div className="text-lg font-semibold">{title}</div>
        <div className="text-xs text-white/50">{subtitle}</div>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="p-10 text-center border border-dashed border-white/10 rounded-xl text-white/50 text-sm">
      {message}
    </div>
  );
}

// ─────────────────── Alerts Tab ───────────────────
function AlertsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/seller/growth/alerts")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <EmptyState message="Loading alerts…" />;
  if (!data?.has_data) return <EmptyState message="Generate a report first to unlock alerts." />;
  const alerts = data.alerts || [];
  if (!alerts.length)
    return (
      <div className="p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
        <CheckCircle2 className="text-emerald-400" />
        <div>
          <div className="font-semibold text-emerald-300">All clear</div>
          <div className="text-xs text-white/60">
            No sales drops, low stock, ad waste, or high returns detected.
          </div>
        </div>
      </div>
    );

  return (
    <div className="space-y-3" data-testid="alerts-list">
      {alerts.map((a, i) => (
        <div
          key={i}
          className={`p-4 rounded-xl border ${PRIORITY_COLOR[a.priority] || PRIORITY_COLOR.low}`}
          data-testid={`alert-${a.type}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="mt-0.5" />
              <div>
                <div className="font-semibold text-white">{a.title}</div>
                <div className="text-xs text-white/70 mt-1">{a.detail}</div>
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-widest shrink-0">{a.priority}</span>
          </div>
          <div className="mt-3 text-xs text-white/80 flex items-center gap-2">
            <ArrowUpRight size={12} /> {a.action}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────── Conversion Tab ───────────────────
function ConversionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("sales"); // sales | conversion | orders
  const [visibleCount, setVisibleCount] = useState(50);

  useEffect(() => {
    api
      .get("/seller/growth/conversion")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <EmptyState message="Loading conversion data…" />;
  if (!data?.has_data) return <EmptyState message={data?.message || "No report yet."} />;

  const all = data.per_sku || [];
  const needle = q.trim().toLowerCase();
  let filtered = needle
    ? all.filter((s) =>
        (s.sku || "").toLowerCase().includes(needle) ||
        (s.style_id || "").toLowerCase().includes(needle) ||
        (s.category || "").toLowerCase().includes(needle)
      )
    : all;
  filtered = [...filtered].sort((a, b) => {
    if (sortKey === "conversion") return (b.conversion_pct || 0) - (a.conversion_pct || 0);
    if (sortKey === "orders") return (b.orders || 0) - (a.orders || 0);
    return (b.sales || 0) - (a.sales || 0);
  });
  const visible = filtered.slice(0, visibleCount);

  return (
    <div className="space-y-6" data-testid="conversion-panel">
      <div className="grid grid-cols-3 gap-4">
        <div className="p-5 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Overall Conversion</div>
          <div className="text-3xl font-bold mt-1 text-[#4F46E5]">{data.overall_conversion_pct}%</div>
          <div className="text-xs text-white/50 mt-1">{data.note}</div>
        </div>
        <div className="p-5 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Gross Orders</div>
          <div className="text-3xl font-bold mt-1">{(data.gross_orders || 0).toLocaleString()}</div>
        </div>
        <div className="p-5 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Delivered Orders</div>
          <div className="text-3xl font-bold mt-1">{(data.delivered_orders || 0).toLocaleString()}</div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Target size={14} className="text-[#4F46E5]" />
            Per-article conversion
            <span className="text-xs text-white/40 font-normal">
              · showing {visible.length} of {filtered.length} articles
              {filtered.length !== all.length ? ` (filtered from ${all.length})` : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setVisibleCount(50); }}
              placeholder="Search SKU / Style ID / category…"
              className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#4F46E5] w-64"
              data-testid="conversion-search-input"
            />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white focus:outline-none focus:border-[#4F46E5]"
              data-testid="conversion-sort-select"
            >
              <option value="sales">Sort: Sales ↓</option>
              <option value="conversion">Sort: Conversion ↓</option>
              <option value="orders">Sort: Orders ↓</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          {visible.map((s, idx) => (
            <div
              key={`${s.sku}-${idx}`}
              className="p-4 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10 flex items-center gap-4"
              data-testid={`conversion-sku-${s.sku}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm truncate text-white">{s.sku}</div>
                <div className="text-[11px] text-white/50 mt-1 font-mono truncate">
                  {s.style_id ? `Style ID · ${s.style_id}` : "—"}
                  {s.category ? `  ·  ${s.category}` : ""}
                </div>
                <div className="text-xs text-white/60 mt-1">
                  {s.orders} orders · {formatINR(s.sales)} sales · {formatINR(s.delivered_sales)} delivered
                </div>
              </div>
              <div className="w-40">
                <div className="h-2 rounded-full bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/10 overflow-hidden">
                  <div
                    className={`h-full ${
                      s.conversion_pct >= 70
                        ? "bg-emerald-400"
                        : s.conversion_pct >= 40
                        ? "bg-amber-400"
                        : "bg-[#4F46E5]"
                    }`}
                    style={{ width: `${Math.min(100, s.conversion_pct)}%` }}
                  />
                </div>
                <div className="text-xs text-white/70 mt-1 text-right">{s.conversion_pct}% conv</div>
              </div>
              <div className="w-64 text-xs text-white/70">{s.recommendation}</div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-white/50 italic p-4">No articles match &ldquo;{q}&rdquo;.</div>
          )}
          {visible.length < filtered.length && (
            <button
              onClick={() => setVisibleCount((n) => n + 50)}
              className="w-full py-2.5 rounded-lg border border-white/[0.08] text-xs text-white/70 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors"
              data-testid="conversion-load-more-btn"
            >
              Load more · {filtered.length - visible.length} remaining
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Decision Engine Tab ───────────────────
function DecisionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/seller/growth/decisions")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <EmptyState message="Loading decisions…" />;
  const list = data?.decisions || [];
  if (!list.length) return <EmptyState message="Generate a report first to see actionable commands." />;

  return (
    <div className="space-y-3" data-testid="decisions-list">
      {list.map((d) => (
        <div
          key={d.sku}
          className="p-4 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10"
          data-testid={`decision-${d.sku}`}
        >
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm">{d.sku}</div>
              <div className="text-xs text-white/50 mt-1">
                {d.orders} orders · {formatINR(d.sales)} · {d.conversion_pct}% conv · ₹{d.avg_price}{" "}
                avg
              </div>
              <div className="text-xs text-white/70 mt-2 italic">{d.reason}</div>
            </div>
            <span
              className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold border ${
                COMMAND_STYLE[d.command] || COMMAND_STYLE.Monitor
              }`}
            >
              {d.command}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────── Creative AI Tab ───────────────────
function CreativePanel() {
  const [recs, setRecs] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null); // {category, products}

  useEffect(() => {
    Promise.all([
      api.get("/seller/growth/creative/recommendations"),
      api.get("/seller/growth/listing-scores"),
    ])
      .then(([r1, r2]) => {
        setRecs(r1.data);
        setScores(r2.data);
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <EmptyState message="Loading creative recommendations…" />;

  const products = scores?.products || [];
  const tips = recs?.tips || [];
  const playbook = recs?.growth_playbook || [];
  const averages = recs?.averages || {};

  // Map each playbook category → the factor we filter weak SKUs by.
  function openDrilldown(category) {
    const factor = {
      Imagery: "image_quality",
      "SEO Title": "title_quality",
      Pricing: "pricing_competitiveness",
      Reviews: "rating_score",
    }[category];
    if (!factor) {
      // Catalog / Performance Marketing apply to all SKUs — show top weakest
      const sorted = [...products]
        .sort((a, b) => (a.listing_score || 0) - (b.listing_score || 0))
        .slice(0, 25);
      setDrilldown({ category, factor: null, products: sorted });
      return;
    }
    const filtered = products
      .filter((p) => (p.factors?.[factor] ?? 100) < 70)
      .sort((a, b) => (a.factors?.[factor] || 0) - (b.factors?.[factor] || 0));
    setDrilldown({ category, factor, products: filtered });
  }

  return (
    <div className="space-y-6" data-testid="creative-panel">
      <div>
        <div className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Sparkles size={14} className="text-[#4F46E5]" /> Creative recommendations
        </div>
        <ul className="space-y-2" data-testid="creative-tips">
          {tips.length === 0 && (
            <li className="text-xs text-white/50 italic">
              No tips yet — sync your catalogue or upload a report.
            </li>
          )}
          {tips.map((t, i) => (
            <li
              key={i}
              className="p-3 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10 text-sm flex gap-3"
            >
              <Wand2 className="text-[#4F46E5] shrink-0 mt-0.5" size={14} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ── ADVANCED LISTING GROWTH PLAYBOOK ── */}
      {playbook.length > 0 && (
        <div data-testid="growth-playbook">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Sparkles size={14} className="text-[#4F46E5]" />
            Listing Growth Playbook — ranked by impact × effort
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {playbook.map((p, i) => {
              const impactColor =
                p.impact === "HIGH"
                  ? "bg-[#4F46E5]/20 text-[#4F46E5] border-[#4F46E5]/40"
                  : p.impact === "MEDIUM"
                  ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                  : "bg-white/10 text-white/60 border-white/20";
              return (
                <div
                  key={i}
                  className="p-4 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10 flex flex-col gap-2"
                  data-testid={`playbook-card-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/45">
                      {p.category}
                    </div>
                    <span
                      className={`text-[9px] font-bold px-2 py-0.5 rounded-md border ${impactColor}`}
                    >
                      {p.impact} IMPACT
                    </span>
                  </div>
                  <div className="text-sm font-bold text-white">{p.title}</div>
                  <div className="text-xs text-white/70 leading-snug">
                    {p.action}
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-white/55">
                    <span className="font-mono-data">
                      Effort: <b className="text-white/80">{p.effort}</b>
                    </span>
                    <span className="font-mono-data">
                      Lift: <b className="text-emerald-300">{p.expected_lift}</b>
                    </span>
                    {p.applies_to > 0 && (
                      <button
                        onClick={() => openDrilldown(p.category)}
                        className="font-mono-data ml-auto text-[#4F46E5] hover:text-white underline-offset-4 hover:underline"
                        data-testid={`drilldown-${i}`}
                      >
                        View {p.applies_to} SKU{p.applies_to === 1 ? "" : "s"} →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {Object.keys(averages).length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { k: "image", label: "Image quality" },
            { k: "title", label: "Title quality" },
            { k: "price", label: "Pricing" },
            { k: "rating", label: "Rating/Reviews" },
          ].map((m) => (
            <div key={m.k} className="p-4 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-white/50">{m.label}</div>
              <div className="text-2xl font-bold mt-1">{averages[m.k] ?? "—"}</div>
              <div className="text-[10px] text-white/40">out of 100</div>
            </div>
          ))}
        </div>
      )}

      <ListingScoresPanel products={products} averages={averages} />

      {drilldown && (
        <DrilldownModal
          drilldown={drilldown}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}

// ─────────────────── Advanced Listing Scores ───────────────────
/* Compute auto fix-suggestion chips from weak factors. Order them by impact
   weight so the most damaging fix surfaces first. */
function fixChips(p) {
  const f = p.factors || {};
  const chips = [];
  if ((f.image_quality ?? 100) < 70) {
    chips.push({ key: "img", icon: "📷", label: "Improve images", tone: "amber" });
  }
  if ((f.title_quality ?? 100) < 70) {
    chips.push({ key: "title", icon: "📝", label: "Rewrite title", tone: "amber" });
  }
  if ((f.pricing_competitiveness ?? 100) < 70) {
    chips.push({ key: "price", icon: "💰", label: "Re-price -10%", tone: "violet" });
  }
  if ((f.rating_score ?? 100) < 70) {
    chips.push({ key: "rating", icon: "⭐", label: "Recover rating", tone: "rose" });
  }
  return chips;
}

function verdictPill(score) {
  if (score >= 80) return { label: "Excellent", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" };
  if (score >= 60) return { label: "Good",      cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" };
  if (score >= 40) return { label: "Needs work", cls: "bg-orange-500/20 text-orange-300 border-orange-500/40" };
  return { label: "Poor", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" };
}

function FactorBar({ label, value, avg }) {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  const safeAvg = Math.max(0, Math.min(100, Number(avg) || 0));
  const tone = safe >= 70 ? "bg-emerald-400" : safe >= 50 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div className="flex items-center gap-2">
      <div className="text-[9px] font-mono uppercase tracking-wider text-white/45 w-7 shrink-0">{label}</div>
      <div className="relative flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${tone}`} style={{ width: `${safe}%` }} />
        {safeAvg > 0 && (
          <div
            className="absolute inset-y-[-2px] w-[1.5px] bg-white/45"
            style={{ left: `${safeAvg}%` }}
            title={`Catalog avg ${safeAvg}`}
          />
        )}
      </div>
      <div className="text-[10px] font-mono text-white/70 w-6 text-right tabular-nums">{safe}</div>
    </div>
  );
}

function ListingScoresPanel({ products, averages }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("score_asc"); // score_asc | score_desc | img | title | price | rating
  const [bucket, setBucket] = useState(null); // null | 'poor' | 'needs' | 'good' | 'excellent'
  const [weakOnly, setWeakOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24);

  // Score-bucket histogram
  const buckets = { poor: 0, needs: 0, good: 0, excellent: 0 };
  for (const p of products) {
    const s = p.listing_score ?? 0;
    if (s >= 80) buckets.excellent += 1;
    else if (s >= 60) buckets.good += 1;
    else if (s >= 40) buckets.needs += 1;
    else buckets.poor += 1;
  }
  const totalP = products.length || 1;
  const histRows = [
    { key: "poor",      label: "Poor (0-39)",       count: buckets.poor,      cls: "bg-rose-500"    },
    { key: "needs",     label: "Needs work (40-59)", count: buckets.needs,    cls: "bg-orange-500" },
    { key: "good",      label: "Good (60-79)",      count: buckets.good,      cls: "bg-amber-500"   },
    { key: "excellent", label: "Excellent (80+)",   count: buckets.excellent, cls: "bg-emerald-500" },
  ];

  // Filter
  const needle = q.trim().toLowerCase();
  let filtered = products.filter((p) => {
    if (needle) {
      const hay = `${p.title || ""} ${p.brand || ""} ${p.product_id || ""} ${p.sku || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    const s = p.listing_score ?? 0;
    if (bucket === "poor"      && !(s < 40))            return false;
    if (bucket === "needs"     && !(s >= 40 && s < 60)) return false;
    if (bucket === "good"      && !(s >= 60 && s < 80)) return false;
    if (bucket === "excellent" && !(s >= 80))           return false;
    if (weakOnly && s >= 60) return false;
    return true;
  });

  // Sort
  filtered = [...filtered].sort((a, b) => {
    if (sortKey === "score_desc") return (b.listing_score || 0) - (a.listing_score || 0);
    if (sortKey === "score_asc")  return (a.listing_score || 0) - (b.listing_score || 0);
    if (sortKey === "img")    return (a.factors?.image_quality || 0) - (b.factors?.image_quality || 0);
    if (sortKey === "title")  return (a.factors?.title_quality || 0) - (b.factors?.title_quality || 0);
    if (sortKey === "price")  return (a.factors?.pricing_competitiveness || 0) - (b.factors?.pricing_competitiveness || 0);
    if (sortKey === "rating") return (a.factors?.rating_score || 0) - (b.factors?.rating_score || 0);
    return 0;
  });

  const visible = filtered.slice(0, visibleCount);
  const avgImg    = averages?.image  ?? 0;
  const avgTitle  = averages?.title  ?? 0;
  const avgPrice  = averages?.price  ?? 0;
  const avgRating = averages?.rating ?? 0;

  function exportCsv() {
    if (filtered.length === 0) {
      toast.error("No rows to export.");
      return;
    }
    const headers = ["product_id_style_id", "brand", "title", "score", "image", "title_quality", "pricing", "rating", "verdict", "url"];
    const rows = filtered.map((p) => [
      p.product_id || "",
      (p.brand || "").replaceAll(",", " "),
      (p.title || "").replaceAll(",", " ").replaceAll("\n", " "),
      p.listing_score ?? "",
      p.factors?.image_quality ?? "",
      p.factors?.title_quality ?? "",
      p.factors?.pricing_competitiveness ?? "",
      p.factors?.rating_score ?? "",
      p.verdict || "",
      p.url || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `listing-scores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filtered.length} listings.`);
  }

  return (
    <div data-testid="listing-scores-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="text-sm font-semibold flex items-center gap-2">
          <ImageIcon size={14} className="text-[#4F46E5]" />
          Listing scores
          <span className="text-xs text-white/40 font-normal">
            · {products.length} listings · showing {visible.length} of {filtered.length}
          </span>
        </div>
        <button
          onClick={exportCsv}
          className="px-3 py-1.5 rounded-md border border-white/[0.08] text-xs font-bold text-white/70 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors flex items-center gap-1.5"
          data-testid="listing-scores-export-csv"
        >
          <DownloadIcon size={12} /> Export CSV
        </button>
      </div>

      {products.length > 0 && (
        <>
          {/* Score distribution histogram — click any band to filter */}
          <div className="p-4 rounded-xl bg-white/[0.025] border border-white/[0.08] mb-4" data-testid="listing-scores-histogram">
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-2">
              <Filter size={11} /> Distribution
              {bucket && (
                <button
                  onClick={() => setBucket(null)}
                  className="ml-auto text-[10px] text-[#4F46E5] hover:underline"
                  data-testid="listing-scores-clear-bucket"
                >
                  Clear filter ×
                </button>
              )}
            </div>
            <div className="flex h-3 rounded-full overflow-hidden bg-white/[0.04]">
              {histRows.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBucket(bucket === b.key ? null : b.key)}
                  className={`${b.cls} transition-opacity ${bucket && bucket !== b.key ? "opacity-30" : "opacity-100"} hover:opacity-100`}
                  style={{ width: `${(b.count / totalP) * 100}%` }}
                  title={`${b.label} · ${b.count} listings`}
                  data-testid={`hist-bar-${b.key}`}
                />
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {histRows.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBucket(bucket === b.key ? null : b.key)}
                  className={`text-left px-2 py-1 rounded-md transition-colors border ${
                    bucket === b.key
                      ? "border-[#4F46E5] bg-[#4F46E5]/10"
                      : "border-transparent hover:border-white/10"
                  }`}
                  data-testid={`hist-label-${b.key}`}
                >
                  <div className="text-[10px] text-white/55">{b.label}</div>
                  <div className="text-sm font-bold text-white tabular-nums">{b.count}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Toolbar: search + sort + weak-only toggle */}
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setVisibleCount(24); }}
                placeholder="Search SKU / Style ID / title / brand…"
                className="w-full pl-8 pr-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#4F46E5]"
                data-testid="listing-scores-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setWeakOnly((v) => !v); setVisibleCount(24); }}
                className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${
                  weakOnly
                    ? "bg-[#4F46E5]/20 border-[#4F46E5] text-white"
                    : "border-white/[0.08] text-white/65 hover:border-[#4F46E5]/60 hover:text-white"
                }`}
                data-testid="listing-scores-weak-only"
              >
                Only weak (&lt;60)
              </button>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white focus:outline-none focus:border-[#4F46E5]"
                data-testid="listing-scores-sort"
              >
                <option value="score_asc">Sort: Score ↑ (worst first)</option>
                <option value="score_desc">Sort: Score ↓ (best first)</option>
                <option value="img">Sort: Weakest image</option>
                <option value="title">Sort: Weakest title</option>
                <option value="price">Sort: Weakest pricing</option>
                <option value="rating">Sort: Weakest rating</option>
              </select>
            </div>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {products.length === 0 && (
          <EmptyState message="No live products yet. Go to Upload and include a brand URL to scrape the live catalogue." />
        )}
        {visible.map((p) => {
          const v = verdictPill(p.listing_score || 0);
          const chips = fixChips(p);
          return (
            <div
              key={p.product_id}
              className="p-3 rounded-xl bg-white/[0.025] backdrop-blur-xl border border-white/10 hover:border-[#4F46E5]/50 hover:bg-white/[0.04] transition-all flex gap-3 group"
              data-testid={`listing-card-${p.product_id}`}
            >
              <div className="relative shrink-0">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-24 h-24 rounded-lg object-cover bg-black/40"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-black/40 flex items-center justify-center">
                    <ImageIcon className="text-white/30" />
                  </div>
                )}
                <div
                  className={`absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-md font-mono text-[10px] font-bold bg-[#0c0c10] border ${
                    (p.listing_score || 0) >= 80 ? "border-emerald-500/40 text-emerald-300"
                    : (p.listing_score || 0) >= 60 ? "border-amber-500/40 text-amber-300"
                    : (p.listing_score || 0) >= 40 ? "border-orange-500/40 text-orange-300"
                    : "border-rose-500/40 text-rose-300"
                  }`}
                >
                  {p.listing_score}/100
                </div>
              </div>

              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-mono text-white/45 truncate">{p.brand}</div>
                    <div className="text-sm font-bold text-white line-clamp-2 leading-tight">{p.title}</div>
                  </div>
                  <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-md font-bold border ${v.cls}`}>
                    {v.label}
                  </span>
                </div>

                <div className="text-[10px] font-mono text-white/55 truncate" data-testid={`listing-style-${p.product_id}`}>
                  Style ID · <span className="text-white/80">{p.product_id || "—"}</span>
                  {p.sku ? <>  ·  SKU · <span className="text-white/80">{p.sku}</span></> : null}
                  {p.mrp ? <>  ·  <span className="text-white/65">{formatINR(p.sale_price || p.mrp)}</span></> : null}
                </div>

                {/* 4 mini factor bars with ghost line for catalog avg */}
                <div className="space-y-1 pt-0.5">
                  <FactorBar label="IMG"  value={p.factors?.image_quality}            avg={avgImg} />
                  <FactorBar label="TXT"  value={p.factors?.title_quality}            avg={avgTitle} />
                  <FactorBar label="PRC"  value={p.factors?.pricing_competitiveness}  avg={avgPrice} />
                  <FactorBar label="RAT"  value={p.factors?.rating_score}             avg={avgRating} />
                </div>

                {/* Auto fix-suggestion chips */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="flex flex-wrap gap-1">
                    {chips.length === 0 ? (
                      <span className="text-[10px] text-emerald-300/80 font-bold inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> All factors healthy
                      </span>
                    ) : chips.map((c) => (
                      <span
                        key={c.key}
                        className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${
                          c.tone === "amber"  ? "bg-amber-500/15 text-amber-300"
                          : c.tone === "violet" ? "bg-[#4F46E5]/15 text-[#A5B4FC]"
                          : "bg-rose-500/15 text-rose-300"
                        }`}
                      >
                        {c.icon} {c.label}
                      </span>
                    ))}
                  </div>
                  {p.url && (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 inline-flex items-center gap-0.5 text-[10px] text-white/55 hover:text-white"
                    >
                      Open <ExternalLink size={9} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && products.length > 0 && (
        <div className="text-xs text-white/50 italic p-4 text-center">
          No listings match your current filters. <button onClick={() => { setQ(""); setBucket(null); setWeakOnly(false); }} className="text-[#4F46E5] hover:underline">Clear all</button>
        </div>
      )}

      {visible.length < filtered.length && (
        <button
          onClick={() => setVisibleCount((n) => n + 24)}
          className="mt-3 w-full py-2.5 rounded-lg border border-white/[0.08] text-xs text-white/70 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors"
          data-testid="listing-scores-load-more"
        >
          Load more · {filtered.length - visible.length} remaining
        </button>
      )}
    </div>
  );
}

// ─────────────────── Drilldown modal ───────────────────
function DrilldownModal({ drilldown, onClose }) {
  const { category, factor, products } = drilldown;
  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="drilldown-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl rounded-2xl bg-[#1A0F11] border border-white/15 shadow-2xl flex flex-col"
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#4F46E5] font-bold">
              {category} drill-down
            </div>
            <div className="text-base font-bold mt-1 text-white">
              {products.length} SKU{products.length === 1 ? "" : "s"} need this fix
            </div>
            {factor && (
              <div className="text-[11px] text-white/70 mt-1">
                Sorted by weakest <b className="text-white">{factor.replace(/_/g, " ")}</b> score first.
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/[0.08] border border-white/15 text-white/80 hover:text-white"
            data-testid="drilldown-close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-2">
          {products.length === 0 && (
            <div className="text-sm text-white/50">
              Nothing to fix here — all SKUs are passing.
            </div>
          )}
          {products.map((p) => (
            <div
              key={p.product_id}
              className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 border border-white/10"
              data-testid={`drilldown-row-${p.product_id}`}
            >
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt={p.title}
                  className="w-14 h-14 rounded-md object-cover shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-md bg-black/40 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-mono text-white/45 truncate">
                  {p.brand}
                </div>
                <div className="text-sm font-bold text-white line-clamp-2">
                  {p.title}
                </div>
                {/* Style ID (Myntra product_id) + seller SKU shown explicitly so
                    sellers can copy them straight into bulk actions / Excel. */}
                <div className="text-[10px] font-mono text-white/55 mt-1 truncate">
                  {p.product_id ? (
                    <span data-testid={`drilldown-style-${p.product_id}`}>
                      Style ID · <span className="text-white/80">{p.product_id}</span>
                    </span>
                  ) : null}
                  {(p.sku || p.seller_sku) ? (
                    <>
                      {p.product_id ? "  ·  " : ""}
                      SKU · <span className="text-white/80">{p.sku || p.seller_sku}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-mono-data text-white">
                  {p.listing_score}/100
                </div>
                {factor && (
                  <div className="text-[10px] text-[#4F46E5] font-bold">
                    {factor}: {p.factors?.[factor]}
                  </div>
                )}
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-1 text-[10px] text-white/55 hover:text-white underline"
                  >
                    Open ↗
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Page ───────────────────
export default function GrowthAI() {
  const [tab, setTab] = useState("alerts");
  return (
    <AppShell role="seller">
      <SectionTitle
        icon={Sparkles}
        title="Growth AI"
        subtitle="Four AI modules that turn your data into actions — alerts, conversion, decisions, creative."
      />

      <div className="flex gap-2 flex-wrap mb-6" data-testid="growth-tabs">
        {TABS.map((t) => (
          <TabButton
            key={t.id}
            active={tab === t.id}
            icon={t.icon}
            label={t.label}
            onClick={() => setTab(t.id)}
            testId={`growth-tab-${t.id}`}
          />
        ))}
      </div>

      {tab === "alerts" && <AlertsPanel />}
      {tab === "conversion" && <ConversionPanel />}
      {tab === "decisions" && <DecisionsPanel />}
      {tab === "creative" && <CreativePanel />}
    </AppShell>
  );
}
